// 주문 내역 카드 컴포넌트
import { Calendar, MapPin, Package, ShoppingBag, Store } from 'lucide-react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { formatRelativeDate } from './constants';
import type { OrderHistoryItem, OrderHistoryTranslations, OrderPaymentState } from './types';
import type { LatestPaymentInfo } from '@/types';
import { useApp } from '@/contexts/AppContext';
import { formatCurrency as formatCurrencyUtil } from '@/utils/currency';

interface OrderHistoryCardProps {
  order: OrderHistoryItem;
  translations: OrderHistoryTranslations;
  onViewReceipt: (order: OrderHistoryItem) => void;
  /**
   * 후불 미결제(timing=postpay && status=pending) 주문에서 "지금 결제" 클릭 시 호출 (C3 #590).
   * 미주입 시 버튼 자체가 렌더되지 않는다(하위 호환 — C2 배지만 사용하는 사용처 보존).
   */
  onPayNow?: (order: OrderHistoryItem) => void;
  /**
   * "지금 결제" 버튼 라벨 (C3 #590).
   * 호출자(OrderHistoryPage)가 `payNowTranslations[locale].button`(15언어 전수)을 주입.
   */
  payNowLabel?: string;
}

/**
 * 후불 미결제 주문 여부 — "지금 결제" 진입점 노출 조건 (C3 #590).
 *
 * C2 `resolvePaymentBadge` 의 미결제 배지 조건과 동일 단일 기준을 사용한다
 * (`timing === 'postpay' && status === 'pending'`). 배지/버튼이 같은 조건에서
 * 함께 노출되도록 SSOT 유지.
 */
function isPostpayUnpaid(
  latestPayment: LatestPaymentInfo | null | undefined,
): boolean {
  return latestPayment?.timing === 'postpay' && latestPayment?.status === 'pending';
}

/**
 * 결제상태 배지 스타일 및 라벨을 계산한다 (C2 #590).
 * - 미결제(후불 pending): 주황/경고 강조
 * - 결제완료: 초록/success
 * - 결제실패: 빨강/destructive
 * - 결제취소: 회색/muted
 * - 처리중: 파랑/파스텔
 * - 환불/부분환불: 보라/파스텔
 * @returns className + label 또는 null (배지 미표시)
 */
function resolvePaymentBadge(
  latestPayment: LatestPaymentInfo | null | undefined,
  t: OrderHistoryTranslations['paymentStatus'],
  paymentState?: OrderPaymentState | null,
): { className: string; label: string } | null {
  if (!latestPayment) return null;

  const { status, isPaid, timing } = latestPayment;

  // 미결제 후불: timing === 'postpay' && status === 'pending'
  if (timing === 'postpay' && status === 'pending') {
    return {
      className: 'bg-orange-100 text-orange-700 border-0',
      label: t.unpaid,
    };
  }

  // 환불/부분환불: isPaid 와 status 가 어긋나는 edge 에서도 환불 배지가 결제완료보다 우선
  if (status === 'refunded') {
    return {
      className: 'bg-purple-100 text-purple-700 border-0',
      label: t.refunded,
    };
  }

  if (status === 'partial_refund') {
    return {
      className: 'bg-purple-100 text-purple-700 border-0',
      label: t.partialRefund,
    };
  }

  // 선불 결제실패: admin #1958 보상취소 흐름과 시각 구분 (라벨 전용, 색상은 일반 실패와 동일).
  if (timing === 'prepay' && status === 'failed') {
    return {
      className: 'bg-red-100 text-red-700 border-0',
      label: t.prepayFailed,
    };
  }

  if (status === 'failed') {
    return {
      className: 'bg-red-100 text-red-700 border-0',
      label: t.failed,
    };
  }

  // 선불 결제취소: 보상취소된 선불 주문 (라벨 전용, 색상은 일반 취소와 동일).
  if (timing === 'prepay' && status === 'cancelled') {
    return {
      className: 'bg-muted text-muted-foreground border-0',
      label: t.prepayCancelled,
    };
  }

  if (status === 'cancelled') {
    return {
      className: 'bg-muted text-muted-foreground border-0',
      label: t.cancelled,
    };
  }

  if (status === 'processing') {
    return {
      className: 'bg-blue-100 text-blue-700 border-0',
      label: t.processing,
    };
  }

  // ★ 분할 결제: 한 주문에 payment 가 여러 개면 `latestPayment` 는 주문을 대표하지
  // 못한다(#854). 160원을 Ana(100)·Beto(60)로 갈랐을 때 Ana 가 먼저 내면
  // `isPaid=true` 가 되어 **Beto 화면에 결제완료가 떴다** — 실제로는 60원이 미수다.
  //
  // 이 검사는 **결제완료 판정 바로 앞**에 있어야 한다. 더 앞에 두면 환불·실패·취소를
  // 가로채는데, 돈이 돌아갔거나 결제가 깨진 사실이 "아직 덜 받았다"보다 손님에게
  // 중요하다(테스트가 이 순서 오류를 잡았다).
  if (paymentState === 'partially_paid') {
    return {
      className: 'bg-orange-100 text-orange-700 border-0',
      label: t.unpaid,
    };
  }

  if (isPaid || status === 'paid') {
    return {
      className: 'bg-green-100 text-green-700 border-0',
      label: t.paid,
    };
  }

  // 그 외 상태(예: prepay pending 등)는 배지 미표시
  return null;
}

export function OrderHistoryCard({
  order,
  translations,
  onViewReceipt,
  onPayNow,
  payNowLabel,
}: OrderHistoryCardProps) {
  const { locale, currency } = useApp();

  const paymentBadge = resolvePaymentBadge(
    order.latestPayment,
    translations.paymentStatus,
    order.paymentState,
  );

  // 후불 미결제 + onPayNow 주입 시에만 "지금 결제" 진입점 노출.
  const showPayNow = Boolean(onPayNow) && isPostpayUnpaid(order.latestPayment);

  return (
    <Card
      className="overflow-hidden bg-background border border-border rounded-[10px] cursor-pointer hover:shadow-lg transition-all duration-200 active:scale-[0.98]"
      onClick={() => onViewReceipt(order)}
    >
      <div className="p-4 rounded-[5px]">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              {order.type === 'pickup' ? (
                <Package className="w-4 h-4 text-secondary shrink-0" />
              ) : (
                <Store className="w-4 h-4 text-primary shrink-0" />
              )}
              <h3 className="text-sm font-bold text-foreground truncate">
                {order.restaurant}
              </h3>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Calendar className="w-3 h-3" />
              <span>{formatRelativeDate(order.date, locale)}</span>
              <span>•</span>
              <span>#{order.orderNumber}</span>
            </div>
          </div>
          {/* 주문유형 배지 + 결제상태 배지 */}
          <div className="flex flex-col items-end gap-1 shrink-0">
            <Badge
              className={`text-[10px] px-2 py-1 rounded-[5px] ${
                order.type === 'pickup'
                  ? 'bg-secondary/10 text-secondary border-0'
                  : 'bg-primary/10 text-primary border-0'
              }`}
            >
              {order.type === 'pickup' ? translations.pickup : translations.table}
            </Badge>
            {paymentBadge && (
              <Badge
                data-testid="payment-status-badge"
                className={`text-[10px] px-2 py-1 rounded-[5px] ${paymentBadge.className}`}
              >
                {paymentBadge.label}
              </Badge>
            )}
          </div>
        </div>

        {/* Table Number */}
        {order.type === 'table' && order.tableNumber && (
          <div className="flex items-center gap-2 mb-3 text-xs px-3 py-2 bg-muted rounded-[5px]">
            <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-foreground">{order.tableNumber}</span>
          </div>
        )}

        {/* Items Summary */}
        <div className="flex items-center justify-between py-3 px-3 bg-muted rounded-[5px]">
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs text-foreground">
              {order.items.reduce((sum, item) => sum + item.quantity, 0)} {translations.items}
            </span>
          </div>
          <div className="text-right">
            <p className="text-[10px] text-muted-foreground mb-0.5">{translations.total}</p>
            <p className="text-base font-bold text-primary">
              {formatCurrencyUtil(order.total, currency)}
            </p>
          </div>
        </div>

        {/* 후불 미결제 "지금 결제" 진입점 (C3 #590) — 카드 onClick(영수증) 버블 차단 */}
        {showPayNow && (
          <button
            type="button"
            data-testid="pay-now-button"
            onClick={(e) => {
              e.stopPropagation();
              onPayNow?.(order);
            }}
            className="mt-3 w-full rounded-[5px] bg-primary py-2.5 text-sm font-bold text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            {payNowLabel}
          </button>
        )}

      </div>
    </Card>
  );
}
